'use client'

import { useEffect, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import { Minus, Plus } from 'lucide-react'

import { Accordion } from '@/lib/api-connector-strapi-cms/api/reeve-landing-page/reeveLandingPageApi.types'
import { Markdown } from '@/lib/ui-kit/components/Markdown.component'

interface FAQAccordionProps {
  faqItems: Accordion[]
}

export default function FAQAccordion({ faqItems }: FAQAccordionProps) {
  const [displayCount, setDisplayCount] = useState(5)
  const [openItem, setOpenItem] = useState('item-0')
  const [prevDisplayCount, setPrevDisplayCount] = useState(5)

  useEffect(() => {
    const timer = setTimeout(() => {
      setPrevDisplayCount(displayCount)
    }, 500)

    return () => clearTimeout(timer)
  }, [displayCount])

  const handleShowMore = () => {
    setDisplayCount((prevCount) => Math.min(prevCount + 5, faqItems.length))
  }

  const toggleItem = (itemId: string) => {
    setOpenItem(openItem === itemId ? '' : itemId)
  }

  const displayedItems = faqItems.slice(0, displayCount)

  return (
    <div className="mx-auto w-full max-w-3xl">
      <div className="w-full">
        <AnimatePresence>
          {displayedItems.map((item, index) => {
            const itemId = `item-${index}`
            const isOpen = openItem === itemId
            const isNewItem = index >= prevDisplayCount

            return (
              <motion.div
                key={index}
                initial={isNewItem ? { opacity: 0, y: 15 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                  delay: isNewItem ? index * 0.08 - prevDisplayCount * 0.08 : 0,
                }}
                className="mb-2 overflow-hidden rounded-lg border border-reeve-divider bg-white last:mb-0 sm:mb-4"
              >
                <button
                  onClick={() => toggleItem(itemId)}
                  className="flex w-full items-center justify-between px-4 py-6 text-left font-medium text-reeve-primary transition-colors duration-150 ease-in-out hover:bg-reeve-divider/20 focus:outline-none"
                  aria-expanded={isOpen}
                >
                  <span className="pr-6 text-[1.125rem] font-semibold leading-[1.5rem]">
                    {item.question}
                  </span>
                  <div className="transition-transform duration-300 ease-out">
                    {isOpen ? (
                      <Minus className="h-5 w-5 text-gray-500" />
                    ) : (
                      <Plus className="h-5 w-5 text-gray-500" />
                    )}
                  </div>
                </button>

                <div
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-4 py-6 pt-2 text-[1rem] font-normal leading-[1.5rem] text-reeve-secondary-gray [&_ul]:pl-4">
                      <Markdown>{item.answer}</Markdown>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>

      {displayCount < faqItems.length && (
        <div className="mt-6 flex justify-center sm:mt-10">
          <motion.button
            onClick={handleShowMore}
            className="group relative inline-flex items-center justify-center overflow-hidden rounded-md border border-reeve-divider bg-transparent px-8 py-3 text-[1.125rem] font-medium leading-[1.25rem] text-reeve-primary transition-colors duration-150 ease-in-out hover:bg-reeve-divider/30 focus:outline-none"
            transition={{ duration: 0.2 }}
          >
            <span className="absolute inset-0 z-0 translate-y-full bg-gradient-to-t from-reeve-primary/[1%] to-transparent transition-transform duration-300 ease-out group-hover:translate-y-0"></span>
            <span className="relative z-10 flex items-center">
              <span className="relative">
                Show more
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-reeve-primary transition-all duration-300 group-hover:w-full"></span>
              </span>
            </span>
          </motion.button>
        </div>
      )}
    </div>
  )
}
